import { hashKey, type QueryKey } from '@tanstack/query-core';
import {
  type UseInfiniteQueryOptions,
  type UseInfiniteQueryResult,
  type UseQueryOptions,
  type UseQueryResult,
  type DefaultError,
  type QueryClient,
  type InfiniteData,
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useEffect, useEffectEvent, useState } from 'react';
import { AxiosError } from 'axios';
import { api401DetectedAtom, isLoggedInAtom } from 'app/atoms';
import { useAtomValue, useSetAtom } from 'jotai';
import { track, Event } from 'app/lib/analytics';
import { storage, StorageKey } from './storage';

const BFF_HYDRATION_MARKER_TTL_MS = 5_000;
const bffHydrationMarkers = new Map<string, { updatedAt: number; expiresAt: number }>();

// Kept as a compatibility hook for login/logout callers. TanStack Query now owns
// request deduplication and cache lifetime; only small one-shot BFF markers remain.
export function clearLastExecution() {
  bffHydrationMarkers.clear();
  return undefined;
}

export function markBffQueryData(queryKey: QueryKey, updatedAt: number): void {
  bffHydrationMarkers.set(hashKey(queryKey), {
    updatedAt,
    expiresAt: Date.now() + BFF_HYDRATION_MARKER_TTL_MS,
  });
}

/**
 * Same as useQuery(), but checks if the user has been logged out, and if so, signals via
 * api401DetectedAtom so that useApi401Logout() (called at the app root) can trigger the logout routine.
 */
export function useQueryLPC<
  TQueryFnData = unknown,
  TError = DefaultError,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(
  options: UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> {
  const setApi401Detected = useSetAtom(api401DetectedAtom);
  const isLoggedIn = useAtomValue(isLoggedInAtom);
  const tanQuery = useQuery(options, queryClient);

  const apiError = tanQuery.error;

  // If the user has been logged out (eg. refresh token expired), signal the logout routine via atom
  useEffect(() => {
    async function handleLogout() {
      if (isLoggedIn && apiError instanceof AxiosError && apiError.response?.status === 401) {
        const currentUserId = await storage.readString(StorageKey.CurrentUserId);
        track(Event.UnexpectedLogout, { source: 'api_401', userId: currentUserId ?? 'null' });
        setApi401Detected(true);
      }
    }
    void handleLogout();
  }, [apiError, isLoggedIn, setApi401Detected]);

  return tanQuery;
}

/**
 * Same as useInfiniteQuery(), except this will reset the pagination when the user loads this query the first time (per session).
 * It also allows you to reset the pagination manually with the resetThenRefetch() method, for example when the user pulls to refresh.
 *
 * Without this, if the user loaded 10 pages of results, then pull-to-refresh (or quits the app and re-opens it), TS Query will send 10 API calls to refetch all 10 pages!
 *
 * How it works:
 * - starts with queryEnabled = false (no auto-fetching);
 * - Wait for cached query data to load (skip if the cache is empty);
 * - resets the pagination: only keep the first page of cached data, and remove the rest;
 * - sets queryEnabled = true, for subsequent pages to load automatically as needed - on scroll;
 * - refetch; the first page only now, since the pagination has been reset.
 *
 * Ref: https://github.com/TanStack/query/discussions/5692
 */
// (options: UseInfiniteQueryOptions<TQueryFnData, TError, TData, TQueryKey, TPageParam>, queryClient?: QueryClient): UseInfiniteQueryResult<TData, TError>;

export function useInfiniteQueryLPC<
  TQueryFnData,
  TError = DefaultError,
  TData = InfiniteData<TQueryFnData>,
  TQueryKey extends QueryKey = QueryKey,
  TPageParam = unknown,
>(
  options: UseInfiniteQueryOptions<TQueryFnData, TError, TData, TQueryKey, TPageParam> & { fetchOnMount?: boolean },
  queryClient?: QueryClient,
): UseInfiniteQueryResult<TData, TError> & {
  resetThenRefetch: () => void;
  resetPagination: () => void;
} {
  const { fetchOnMount = false, ...queryOptions } = options;
  const client = useQueryClient(queryClient);
  const defaultedOptions = client.defaultQueryOptions(queryOptions);
  const queryHash = defaultedOptions.queryHash;
  const [initializedQuery, setInitializedQuery] = useState<{ client: QueryClient; hash: string }>();
  const queryEnabled = initializedQuery?.client === client && initializedQuery.hash === queryHash;
  const resolvedOptions = {
    ...queryOptions,
    enabled: queryEnabled ? (defaultedOptions.enabled ?? true) : false,
  };

  const tanQuery = useInfiniteQuery(resolvedOptions, client);
  const { refetch } = tanQuery;

  const resetPagination = () => resetInfinitePagination(client, queryOptions.queryKey);

  const resetThenRefetch = () => {
    resetPagination();
    // Need to wait a few ms (10 doesn't work, 100 works...)
    // I guess because client.setQueryData(), called in resetPagination(), needs some time to update the query cache,
    // and calling refetch too quickly will cause issues, namely query.data not being updated with the values that were received from the API!
    setTimeout(() => void refetch(), 100);
  };

  const prepareQuery = useEffectEvent((queryKey: QueryKey) => {
    resetInfinitePagination(client, queryKey);
    if (fetchOnMount) {
      // Make even fresh cached data eligible for the automatic fetch on enable.
      // TanStack then owns the request and respects the caller's enabled option.
      void client.invalidateQueries({ queryKey, exact: true, refetchType: 'none' });
    }
  });

  // Reset each newly selected query before enabling it. Equivalent key arrays
  // share a hash, so cache notifications do not restart initialization. Mount
  // options are read during preparation without restarting an initialized query.
  useEffect(() => {
    const queryKey = client.getQueryCache().get(queryHash)?.queryKey;
    if (!queryKey) return;
    prepareQuery(queryKey);
    const enableTimer = setTimeout(() => setInitializedQuery({ client, hash: queryHash }), 0);
    return () => clearTimeout(enableTimer);
  }, [client, queryHash]);

  return { ...tanQuery, resetThenRefetch, resetPagination };
}

function resetInfinitePagination(client: QueryClient, queryKey: QueryKey) {
  client.setQueryData<InfiniteData<unknown>>(queryKey, (oldData) => {
    if (!oldData) return undefined;
    if (oldData.pages.length < 1) {
      return oldData;
    }
    if (oldData.pages.length === 1) {
      return { ...oldData, pageParams: [null] };
    }
    return {
      ...oldData,
      pages: oldData.pages.slice(0, 1),
      pageParams: [null],
    };
  });
}

export function useAppDidStart() {
  const appDidStart = () => undefined;
  const appDidStop = () => undefined;

  return {
    appDidStart,
    appDidStop,
  };
}

// Query keys do not encode their cached data type; callers provide the expected TanStack cache type.

export function getBffQueryData<T>(client: QueryClient, queryKey: QueryKey): T | undefined {
  const markerKey = hashKey(queryKey);
  const marker = bffHydrationMarkers.get(markerKey);
  if (!marker) return undefined;

  bffHydrationMarkers.delete(markerKey);
  if (marker.expiresAt < Date.now()) return undefined;

  const state = client.getQueryState<T>(queryKey);
  if (!state || state.isInvalidated || state.dataUpdatedAt !== marker.updatedAt) return undefined;
  return state.data;
}
